import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { HEADER_FIELDS } from '../constants'
import HeaderFieldRow from './HeaderFieldRow'
import ColumnHeader from './ColumnHeader'
import AddColumnButton from './AddColumnButton'
import { eventBus, EVENTS } from '../../../../../utils/EventBus'

const TableHeader = ({
  columns,
  allColumns,
  hiddenColumnKeys,
  hiddenRowKeys,
  hiddenHeaderFieldKeys,
  onUnhideItem,
  baseColumns,
  customerColumns,
  totalColumnCount,
  columnManager,
  todayString,
  getStickyLeft,
  buyerLeft,
  selectedColumn,
  selectedColumnIds,
  setSelectedColumn,
  setSelectedRow,
  handleColumnContextMenu,
  handleColumnSelection,
  handleHeaderRowContextMenu,
  handleHeaderFieldSelection,
  selectedHeaderFieldKeys = [],
  handleMouseDown,
  newColumnId,
  newColumnInputRefs,
  hasAutoFocused,
  handleCustomerCodeChange,
  handleCustomerCodeEmptyLabel,
  handleAddNewColumn,
  handleHeaderFieldKeyDown,
  handleHeaderFieldKeyUp,
  handleHeaderFieldBlur,
  handleHeaderFieldToggle,
  isDisabled,
  selectedItems,
  distributionManager,
  orders,
  productName,
  saleMan,
  hasOriginalOrderQuantity,
  editingColumnId,
  setEditingColumnId,
  handleDragStart,
  handleDragEnd,
  handleDragOver,
  handleDragLeave,
  handleDrop,
  draggedColumnId,
  dropTargetId,
  isAtEnd,
  handleRowDragOver,
  draggedRowIdx,
  rowReorderDisabled = false,
  gradeFilterValues = [],
  sizeFilterValues = [],
  gradeFilterOptions = [],
  sizeFilterOptions = [],
  onGradeFilterChange,
  onSizeFilterChange,
  isFilterLoading = false,
  isReadOnly = false
}) => {
  const [, setProfitRevision] = useState(0)
  const [headerFieldLayout, setHeaderFieldLayout] = useState({
    offsets: {},
    totalHeight: 0
  })
  const headerFieldRowRefs = useRef({})

  useEffect(() => {
    const refreshProfit = () => setProfitRevision((revision) => revision + 1)
    const unsubChanged = eventBus.on(EVENTS.DISTRIBUTION_CHANGED, refreshProfit)
    const unsubCommitted = eventBus.on(
      EVENTS.DISTRIBUTION_COMMITTED,
      refreshProfit
    )
    const unsubReset = eventBus.on(EVENTS.DISTRIBUTION_RESET, refreshProfit)

    return () => {
      unsubChanged()
      unsubCommitted()
      unsubReset()
    }
  }, [])

  const totalProfit = distributionManager.calculateTotalProfit(selectedItems)
  const lastVisibleHeaderFieldKey = [...HEADER_FIELDS]
    .reverse()
    .find((field) => !hiddenHeaderFieldKeys.has(field.key))?.key
  const fixedBaseColumnCount = Math.max(baseColumns.length - 1, 1)
  const remainColumnIndex = columns.findIndex((column) => column.id === 'remain')
  const legendWidth =
    remainColumnIndex > 0
      ? getStickyLeft(remainColumnIndex + 1) - getStickyLeft(1)
      : undefined
  useLayoutEffect(() => {
    const visibleFields = HEADER_FIELDS.filter(
      (field) => !hiddenHeaderFieldKeys.has(field.key)
    )
    const rows = visibleFields
      .map((field) => ({
        key: field.key,
        element: headerFieldRowRefs.current[field.key]
      }))
      .filter(({ element }) => element)

    const updateLayout = () => {
      let totalHeight = 0
      const offsets = {}
      rows.forEach(({ key, element }) => {
        offsets[key] = totalHeight
        totalHeight += element.getBoundingClientRect().height
      })
      setHeaderFieldLayout((current) => {
        const unchanged =
          current.totalHeight === totalHeight &&
          visibleFields.every((field) => current.offsets[field.key] === offsets[field.key])
        return unchanged ? current : { offsets, totalHeight }
      })
    }

    updateLayout()
    const observer = new ResizeObserver(updateLayout)
    rows.forEach(({ element }) => observer.observe(element))
    return () => observer.disconnect()
  }, [hiddenHeaderFieldKeys, customerColumns.length])

  return (
    <thead
      onDragOver={(e) => !isReadOnly && !rowReorderDisabled && handleRowDragOver(e, null)}
    >
      <tr>
        <th className='table-name-header' colSpan={totalColumnCount + 1}>
          荷割結果テーブル
        </th>
      </tr>

      <tr>
        <th
          className='group-header'
          style={{ width: `${columnManager.getWidth('no')}px` }}
        >
          売日
        </th>
        <th className='group-header' colSpan={2}>
          {todayString}
        </th>
        <th className='group-header'>セリ人</th>
        <th className='group-header' colSpan={4}>
          {String(saleMan || '')
            .replace(/\d{4}[-/]\d{2}[-/]\d{2}/, '')
            .trim()}
        </th>
        <th
          className='group-header'
          colSpan={Math.max(totalColumnCount - 8, 1)}
        ></th>
        <th className='group-header'>
          合計損益 {totalProfit?.toLocaleString('ja-JP')}
        </th>
      </tr>

      <tr>
        <th className='sub-header text-left px-2' colSpan={totalColumnCount}>
          {productName}
        </th>
        <th className='sub-header'></th>
      </tr>

      {HEADER_FIELDS.map((field, idx) => {
        if (hiddenHeaderFieldKeys.has(field.key)) return null

        let hiddenBefore = []
        for (let i = idx - 1; i >= 0; i--) {
          if (hiddenHeaderFieldKeys.has(HEADER_FIELDS[i].key)) {
            hiddenBefore.push(HEADER_FIELDS[i].key)
          } else {
            break
          }
        }

        let hiddenAfter = []
        for (let i = idx + 1; i < HEADER_FIELDS.length; i++) {
          if (hiddenHeaderFieldKeys.has(HEADER_FIELDS[i].key)) {
            hiddenAfter.push(HEADER_FIELDS[i].key)
          } else {
            break
          }
        }

        // Danh sách các key hiện đang hiển thị (chưa ẩn) để tính range selection
        const visibleFieldKeys = HEADER_FIELDS
          .filter((f) => !hiddenHeaderFieldKeys.has(f.key))
          .map((f) => f.key)

        return (
          <HeaderFieldRow
            key={field.key}
            fieldKey={field.key}
            label={field.label}
            defaultOverride={field.defaultOverride}
            fixedBaseColumnCount={fixedBaseColumnCount}
            fixedBaseLeft={getStickyLeft(1)}
            legendWidth={legendWidth}
            buyerLeft={buyerLeft}
            customerColumns={customerColumns}
            columnManager={columnManager}
            handleHeaderFieldKeyDown={handleHeaderFieldKeyDown}
            handleHeaderFieldKeyUp={handleHeaderFieldKeyUp}
            handleHeaderFieldBlur={handleHeaderFieldBlur}
            handleHeaderFieldToggle={handleHeaderFieldToggle}
            isDisabled={isDisabled || isReadOnly}
            selectedItems={selectedItems}
            distributionManager={distributionManager}
            orders={orders}
            hasOriginalOrderQuantity={hasOriginalOrderQuantity}
            hiddenBefore={hiddenBefore}
            hiddenAfter={hiddenAfter}
            onUnhide={(key) => onUnhideItem('headerField', key)}
            onContextMenu={(e) =>
              handleHeaderRowContextMenu(e, field.key, field.label)
            }
            isSelected={selectedHeaderFieldKeys.includes(field.key)}
            onSelect={
              handleHeaderFieldSelection
                ? (e) => handleHeaderFieldSelection(field.key, e, visibleFieldKeys)
                : undefined
            }
            rowRef={(element) => {
              if (element) headerFieldRowRefs.current[field.key] = element
              else delete headerFieldRowRefs.current[field.key]
            }}
            stickyTop={headerFieldLayout.offsets[field.key] ?? 0}
            showDanwareLabel={lastVisibleHeaderFieldKey === field.key}
          />
        )
      })}

      <tr>
        <ColumnHeader
          columns={columns}
          allColumns={allColumns}
          hiddenColumnKeys={hiddenColumnKeys}
          hiddenRowKeys={hiddenRowKeys}
          hiddenHeaderFieldKeys={hiddenHeaderFieldKeys}
          onUnhideItem={onUnhideItem}
          selectedItems={selectedItems}
          getStickyLeft={getStickyLeft}
          buyerLeft={buyerLeft}
          selectedColumn={selectedColumn}
          selectedColumnIds={selectedColumnIds}
          setSelectedColumn={setSelectedColumn}
          setSelectedRow={setSelectedRow}
          handleColumnContextMenu={handleColumnContextMenu}
          handleColumnSelection={handleColumnSelection}
          handleMouseDown={handleMouseDown}
          newColumnId={newColumnId}
          newColumnInputRefs={newColumnInputRefs}
          hasAutoFocused={hasAutoFocused}
          handleCustomerCodeChange={handleCustomerCodeChange}
          handleCustomerCodeEmptyLabel={handleCustomerCodeEmptyLabel}
          orders={orders}
          hasOriginalOrderQuantity={hasOriginalOrderQuantity}
          editingColumnId={editingColumnId}
          setEditingColumnId={setEditingColumnId}
          handleDragStart={handleDragStart}
          handleDragEnd={handleDragEnd}
          handleDragOver={handleDragOver}
          handleDragLeave={handleDragLeave}
          handleDrop={handleDrop}
          draggedColumnId={draggedColumnId}
          dropTargetId={dropTargetId}
          isAtEnd={isAtEnd}
          gradeFilterValues={gradeFilterValues}
          sizeFilterValues={sizeFilterValues}
          gradeFilterOptions={gradeFilterOptions}
          sizeFilterOptions={sizeFilterOptions}
          onGradeFilterChange={onGradeFilterChange}
          onSizeFilterChange={onSizeFilterChange}
          isFilterLoading={isFilterLoading}
          isReadOnly={isReadOnly}
          stickyTop={headerFieldLayout.totalHeight}
        />
        <AddColumnButton
          onAddNewColumn={handleAddNewColumn}
          disabled={isReadOnly}
          stickyTop={headerFieldLayout.totalHeight}
        />
      </tr>
    </thead>
  )
}

export default TableHeader
